/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import { marked } from 'marked';
import DOMPurify from 'dompurify';

var escape_txt = document.createElement('textarea');

function escapeHTML(html) {
  escape_txt.textContent = html;
  return escape_txt.innerHTML;
}
function unEscapeHTML(html) {
  let new_value = html;
  var i = 0;
  while(true) {
    escape_txt.innerHTML = html;
    new_value = escape_txt.textContent;
    if(html === new_value || i > 100) {
      break;
    }
    html = new_value;
    i++;
  }
  return new_value;
}

function strip(str) {
  return str.replace(/^\s+|\s+$/g, '');
}

const stripMarkdown = (markdownText) => {
  try {
    // Check if the input is a string
    if (typeof markdownText !== 'string') {
      throw new Error('Input must be a string');
    }

    // Use marked to parse the markdown text
    const html = marked(markdownText);
    
    // Create a temporary DOM element to hold the HTML
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    
    // Extract and return the text content
    return tempElement.textContent || tempElement.innerText || '';
  } catch (error) {
    return markdownText; // Return an empty string in case of an error
  }
};

/**
 * Sanitizes HTML content by allowing only specified tags and attributes.
 * 
 * @param {string} html - The HTML content to sanitize.
 * @param {Array<string>} allowedTags - The list of allowed tags. Defaults to ['br', 'ins', 'del'].
 * @returns {string} - The sanitized HTML content.
 */
function sanitizeHtml(html, allowedTags = ['br', 'ins', 'del', 'a']) {
  // Configure DOMPurify to allow only specific tags and attributes
  const config = {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: ['href', 'target', 'class'],
    FORBID_ATTR: ['style', 'on*', 'src', 'data-*'], // Forbid any inline styles, event handlers, src, and data attributes
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed'] // Forbid potentially dangerous tags
  };

  // Sanitize the HTML content
  const cleanHtml = DOMPurify.sanitize(html, config);

  // Ensure that 'a' tags have target="_blank" and no JavaScript in href
  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanHtml, 'text/html');
  const links = doc.querySelectorAll('a');

  const allowedProtocols = ['http:', 'https:'];

  links.forEach(link => {
    const href = link.getAttribute('href');
    if (href) {
      try {
        const url = new URL(href, 'http://example.com'); // Use a dummy base URL to parse the href
        if (!allowedProtocols.includes(url.protocol)) {
          link.removeAttribute('href');
        } else {
          link.setAttribute('target', '_blank');
        }
      } catch (e) {
        // If URL parsing fails, remove the href attribute
        link.removeAttribute('href');
      }
    }
  });

  return doc.body.innerHTML;
}

function sanitizeMermaid(html) {
  // Configure DOMPurify to allow specific tags and attributes needed for Mermaid
  const config = {
    ALLOWED_TAGS: ['svg', 'g', 'path', 'text', 'rect', 'circle', 'polygon', 'line', 'title', 'style'],
    ALLOWED_ATTR: ['class', 'id', 'fill', 'stroke', 'd', 'x', 'y', 'cx', 'cy', 'r', 'points', 'x1', 'x2', 'y1', 'y2', 'font-family', 'font-size', 'text-anchor', 'transform', 'viewBox', 'xmlns', 'width', 'height', 'style'],
    FORBID_ATTR: ['on*', 'src', 'data-*'] // Forbid any event handlers, src, and data attributes
  };

  // Sanitize the HTML content
  const cleanHtml = DOMPurify.sanitize(html, config);

  return cleanHtml;
}
function checkHref(href) {
  const allowedProtocols = ['http:', 'https:', 'mailto:'];
  if (href) {
    try {
      const url = new URL(href, 'http://example.com'); // Use a dummy base URL to parse the href
      if (!allowedProtocols.includes(url.protocol)) {
        return null;
      } else {
        return href;
      }
    } catch (e) {
      // If URL parsing fails, remove the href attribute
      return null;
    }
  }
  return null;
}

function urlify(text) {
  // Regular expression to match URLs with the condition that the character before https:// is not >, ' or "
  var urlRegex = /(^|[^>'"`])(https:\/\/[^\s'"><]+)([.)]?)(?=['"><!?.)]*)/g;

  // Replace the matched URLs with <a href=""> actual link
  return text.replace(urlRegex, function(match, p1, p2, p3) {
      // p1 is the character before the URL (if any)
      // p2 is the URL itself
      // p3 is the trailing character (if it's . or ))
      return p1 + '<a href="' + p2 + '">' + p2 + '</a>' + (p3 === '.' || p3 === ')' ? p3 : '');
  });
}


export { strip, escapeHTML, unEscapeHTML, stripMarkdown, sanitizeHtml, checkHref, sanitizeMermaid, urlify };
